import {isUndefined} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {ClickEventHandler} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownEmpty} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {dropdownListVerticalPadding} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownLoader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownLoader';
import {
  dropdownListDefaultRowHeight,
  DropdownVirtualList,
  ScrollContext,
} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownVirtualList';
import {
  SearchDropdownHeader,
  searchDropdownHeaderHeight,
} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdownHeader';
import {computeIsNearBottomOfScrollableList} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdownHelpers';
import {AsyncLoader} from '../../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {FilterApplyFooter, FilterApplyFooterProps} from '../common/filterApplyFooter';
import {FilterRightClearButton} from '../common/filterClearRightButton';
import {
  BaseFilterDropdownProps,
  FilterResourceResults,
  FilterRow,
  MultiSelectFilterBaseProps,
} from '../common/types';
import {FilterDropdownRow} from './filterDropdownRow';

/*
 * Props.
 */

export interface MultiSelectFilterDropdownProps extends MultiSelectFilterBaseProps, BaseFilterDropdownProps {
  // Optional prop, if included the filter will have a footer with buttons
  // apply/cancel the current filters
  // No data related to the current selection is stored within this component, clients
  // of this component are expected to handle async updates as needed.
  footerHandlers?: FilterApplyFooterProps;
  clearButtonContent?: ReactNode;
  searchPlaceholder?: string;
  initialSelectedItemIds?: ReadonlyArray<number>;
  shouldStayOpenOnClear?: boolean;
  rightIcon?: ReactNode;
}

/*
 * Constants.
 */

const defaultDropdownWidth = 250;
const simpleDropdownHeaderHeight = 40;

/*
 * Style.
 */

const StyledLoaderDiv = styled.div`
  height: 100%;
  ${center()};
`;

/*
 * Component.
 */

export const MultiSelectFilterDropdown: FC<MultiSelectFilterDropdownProps> = (props) => {
  const {
    title,
    searchProps,
    footerHandlers,
    showClearButton,
    width,
    onClear,
    clearButtonContent,
    shouldStayOpenOnClear = false,
  } = props;

  const onClearClicked: ClickEventHandler = (event) => {
    if (props.onRequestClose && !shouldStayOpenOnClear) {
      props.onRequestClose();
    }
    onClear(event);
  };

  const rightIcon = showClearButton ? (
    <FilterRightClearButton onClear={onClearClicked}>{clearButtonContent}</FilterRightClearButton>
  ) : undefined;

  return (
    <Dropdown {...props} width={width ?? defaultDropdownWidth}>
      {searchProps ? (
        <SearchDropdownHeader
          title={title}
          value={searchProps.searchQuery}
          onChange={searchProps.setSearchQuery}
          shouldIncludeSearchIcon
          placeholder={props.searchPlaceholder ?? 'Search...'}
          rightIcon={rightIcon || props.rightIcon}
        />
      ) : (
        <DropdownHeader title={title} rightIcon={rightIcon} />
      )}
      {renderContent(props, !isUndefined(searchProps), !isUndefined(footerHandlers))}
      {footerHandlers && (
        <FilterApplyFooter
          onApplyClicked={footerHandlers.onApplyClicked}
          onCancelClicked={footerHandlers.onCancelClicked}
          applyButtonLabel={footerHandlers.applyButtonLabel}
          isApplyDisabled={footerHandlers.isApplyDisabled}
          onRequestClose={props.onRequestClose}
        />
      )}
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function renderContent(props: MultiSelectFilterDropdownProps, hasSearch: boolean, hasApplyFooter: boolean) {
  const {rows, results, height, width, onRequestClose, isMultiSelect = true} = props;

  // Loading state.
  if (results && results.asyncStatus !== AsyncStatusesEnum.LOADED) {
    return <DropdownLoader status={results.asyncStatus} onRetryClick={results.onLoadingRetryClicked} />;
  }

  // Empty state.
  if (rows.length === 0) {
    return results?.renderEmpty ? results.renderEmpty : <DropdownEmpty />;
  }

  // Add an extra item at the end for the loader if needed.
  const rowCount = rows.length + (results && results.canLoadMore ? 1 : 0);

  const onScroll = (scrollContext: ScrollContext) => {
    const isNearBottom = computeIsNearBottomOfScrollableList(scrollContext);
    if (!isNearBottom) {
      return;
    }

    if (results && results.canLoadMore && results.loadMore) {
      results.loadMore();
    }
  };

  const maxHeight = computeMaxDropdownListHeight(hasSearch, height);

  // Having an apply footer overrides overrides the value of isMultiSelect when determining
  // if the filter should remain open on selection.
  const shouldCloseOnSelection = hasApplyFooter ? false : !isMultiSelect;

  return (
    <DropdownVirtualList
      key={results?.key ?? 'multi-select-dropdown-list-key'}
      width={width ?? defaultDropdownWidth}
      maxHeight={maxHeight}
      rowCount={rowCount}
      renderRow={(index) => renderResultsRow(rows, index, isMultiSelect, shouldCloseOnSelection, results)}
      getRowHeight={(index) => getRowHeight(rows, index)}
      onRequestClose={onRequestClose}
      onScroll={onScroll}
    />
  );
}

function getRowHeight(rows: ReadonlyArray<FilterRow>, index: number) {
  return rows[index]?.height ?? undefined;
}

function renderResultsRow(
  rows: ReadonlyArray<FilterRow>,
  index: number,
  isMultSelect: boolean,
  shouldCloseOnSelection: boolean,
  results?: FilterResourceResults,
) {
  // Render loader for infinite scroll in the async case
  if (results && results.canLoadMore && results.loadMore && index === rows.length) {
    return (
      <StyledLoaderDiv>
        <AsyncLoader
          status={AsyncStatusesEnum.LOADING}
          size={VisualSizesEnum.MEDIUM}
          onRetryClick={results.loadMore}
        />
      </StyledLoaderDiv>
    );
  }

  // Otherwise, render a regular row.
  return (
    <FilterDropdownRow
      row={rows[index]}
      isMultiSelect={isMultSelect}
      shouldCloseOnSelection={shouldCloseOnSelection}
    />
  );
}

function computeMaxDropdownListHeight(hasSearch: boolean, height?: number) {
  // This 8.5 multiplier sets the height of the dropdown such that the 9th item is exactly half visible,
  // to help act as an affordance for scrolling so the user knows the list continues
  const defaultMaxHeight = Math.round(dropdownListVerticalPadding + dropdownListDefaultRowHeight * 8.5);

  const headerHeight = hasSearch ? searchDropdownHeaderHeight : simpleDropdownHeaderHeight;
  return height ? height - headerHeight : defaultMaxHeight;
}
